import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  CircularProgress, 
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Switch,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { isUserAdmin } from '../../api/trackingConfig';

/**
 * Component for managing user roles
 * This is a placeholder component that would be implemented with actual user management functionality
 */
function UserRoleManagement() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [users, setUsers] = useState([
    { id: '1', email: 'admin@example.com', role: 'admin', isAdmin: true },
    { id: '2', email: 'user@example.com', role: 'user', isAdmin: false },
    { id: '3', email: 'manager@example.com', role: 'manager', isAdmin: true }
  ]);
  const [newUser, setNewUser] = useState({ email: '', role: 'user' });
  const [showAddForm, setShowAddForm] = useState(false);

  // In a real implementation, this would fetch users from the backend
  useEffect(() => {
    // Placeholder for fetching users
    setLoading(true);
    
    // Simulate API call
    setTimeout(() => {
      setLoading(false);
    }, 500);
  }, []);

  // Handle admin toggle
  const handleAdminToggle = (userId) => {
    // In a real implementation, this would update the user's admin status in the backend
    setUsers(users.map(user => 
      user.id === userId ? { ...user, isAdmin: !user.isAdmin } : user
    ));
  };

  // Handle role change
  const handleRoleChange = (userId, newRole) => {
    // In a real implementation, this would update the user's role in the backend
    setUsers(users.map(user => 
      user.id === userId ? { ...user, role: newRole } : user
    ));
  };

  // Handle new user form change
  const handleNewUserChange = (field, value) => {
    setNewUser(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle add user
  const handleAddUser = () => {
    // Validate
    if (!newUser.email) {
      setError('Email is required');
      return;
    }

    // In a real implementation, this would add the user to the backend
    const newId = Math.random().toString(36).substring(2, 9);
    setUsers([...users, { 
      id: newId, 
      email: newUser.email, 
      role: newUser.role,
      isAdmin: newUser.role === 'admin'
    }]);

    // Reset form
    setNewUser({ email: '', role: 'user' });
    setShowAddForm(false);
    setError(null);
  };

  // If loading, show loading indicator
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', p: 3 }}>
      <Typography variant="h4" gutterBottom>
        User Role Management
      </Typography>
      
      <Alert severity="info" sx={{ mb: 3 }}>
        This is a placeholder component. In a real implementation, this would connect to your user management system.
      </Alert>
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setShowAddForm(!showAddForm)}
        >
          {showAddForm ? 'Cancel' : 'Add User'}
        </Button>
      </Box>
      
      {showAddForm && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Add New User
          </Typography>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Email"
              value={newUser.email}
              onChange={(e) => handleNewUserChange('email', e.target.value)}
              fullWidth
            />
            
            <FormControl fullWidth>
              <InputLabel id="role-label">Role</InputLabel>
              <Select
                labelId="role-label"
                id="role-select"
                value={newUser.role}
                label="Role"
                onChange={(e) => handleNewUserChange('role', e.target.value)}
              >
                <MenuItem value="user">User</MenuItem>
                <MenuItem value="manager">Manager</MenuItem>
                <MenuItem value="admin">Admin</MenuItem>
              </Select>
            </FormControl>
            
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
              <Button
                variant="contained"
                onClick={handleAddUser}
              >
                Add User
              </Button>
            </Box>
          </Box>
        </Paper>
      )}
      
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>
          Users
        </Typography>
        
        <TableContainer>
          <Table sx={{ minWidth: 650 }} aria-label="user role table">
            <TableHead>
              <TableRow>
                <TableCell>Email</TableCell>
                <TableCell>Role</TableCell>
                <TableCell align="center">Admin</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((user) => (
                <TableRow
                  key={user.id}
                  sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                >
                  <TableCell component="th" scope="row">
                    {user.email}
                  </TableCell>
                  <TableCell>
                    <FormControl sx={{ minWidth: 120 }}>
                      <Select
                        value={user.role}
                        onChange={(e) => handleRoleChange(user.id, e.target.value)}
                        size="small"
                      >
                        <MenuItem value="user">User</MenuItem>
                        <MenuItem value="manager">Manager</MenuItem>
                        <MenuItem value="admin">Admin</MenuItem>
                      </Select>
                    </FormControl>
                  </TableCell>
                  <TableCell align="center">
                    <Switch
                      checked={user.isAdmin}
                      onChange={() => handleAdminToggle(user.id)}
                      color="primary"
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
}

export default UserRoleManagement;
